// Inspection Items admin — sits inside MaintenanceSetupPage as a tab.
//
// Two purposes:
//   1. CRUD the items that appear on the daily inspection sheet.
//   2. Toggle the is_critical flag per item. Critical items that fail
//      flip the truck to 'down' and bump the auto-spawned WO to 'urgent'.
//      Non-critical fails still spawn a WO but don't ground the truck.
//
// Per Matt's call: is_critical is internal to the form setup, NOT shown
// on the driver-facing inspection sheet. Drivers answer honestly; the
// system decides the consequences.

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';

const RESPONSE_TYPES = [
  { value: 'pass_fail', label: 'Pass / Fail' },
  { value: 'ok_low',    label: 'OK / Low (fluid checks)' },
  { value: 'yes_no',    label: 'Yes / No (damage, warning lights)' },
];

const EMPTY_DRAFT = {
  name: '',
  category: 'general',
  response_type: 'pass_fail',
  allow_na: false,
  is_critical: false,
  sort_order: 0,
};

export default function InspectionItemsAdminPage() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getAllInspectionItems();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load inspection items');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!can('manage_maintenance')) {
    return <p style={{ padding: 16, color: 'var(--text-muted)' }}>You do not have permission to manage inspection items.</p>;
  }

  // Toggle a single field on a single row and persist. Used for the
  // Critical checkbox in the table — one click, immediate save.
  async function toggleField(item, field) {
    setSavingId(item.id);
    setError('');
    try {
      const updated = await api.updateInspectionItem(item.id, { [field]: !item[field] });
      setItems(prev => prev.map(i => i.id === item.id ? updated : i));
    } catch (err) {
      setError(err.message || `Failed to update ${field}`);
    }
    setSavingId(null);
  }

  async function archive(item) {
    if (!window.confirm(`Archive "${item.name}"? It will no longer appear on the driver inspection sheet. Existing inspections that reference it stay unchanged.`)) return;
    setSavingId(item.id);
    try {
      const updated = await api.updateInspectionItem(item.id, { is_active: false });
      setItems(prev => prev.map(i => i.id === item.id ? updated : i));
    } catch (err) {
      setError(err.message || 'Failed to archive');
    }
    setSavingId(null);
  }

  async function unarchive(item) {
    setSavingId(item.id);
    try {
      const updated = await api.updateInspectionItem(item.id, { is_active: true });
      setItems(prev => prev.map(i => i.id === item.id ? updated : i));
    } catch (err) {
      setError(err.message || 'Failed to restore');
    }
    setSavingId(null);
  }

  async function createItem(e) {
    e.preventDefault();
    if (!draft.name.trim()) return;
    setSavingId('new');
    setError('');
    try {
      const created = await api.createInspectionItem({
        ...draft,
        name: draft.name.trim(),
        category: draft.category.trim() || 'general',
        sort_order: Number(draft.sort_order) || 0,
      });
      setItems(prev => [...prev, created]);
      setDraft(EMPTY_DRAFT);
      setShowAdd(false);
    } catch (err) {
      setError(err.message || 'Failed to create item');
    }
    setSavingId(null);
  }

  const active = items.filter(i => i.is_active);
  const archived = items.filter(i => !i.is_active);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h2 style={{ margin: 0 }}>Inspection Items</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            Items on the daily driver inspection. Mark <strong>Critical</strong> to ground the truck when that item fails.
          </p>
        </div>
        <button className="btn btn-gold" onClick={() => setShowAdd(s => !s)}>
          {showAdd ? 'Cancel' : '+ Add Item'}
        </button>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}

      {/* Inline create row — appears under the header when "+ Add Item"
          is clicked. Keeps the page in one scroll without a modal. */}
      {showAdd && (
        <form onSubmit={createItem} style={{
          background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
          padding: 16, marginBottom: 16, display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12,
        }}>
          <label style={{ gridColumn: '1 / -1' }}>
            <div style={lblStyle}>Item Name *</div>
            <input className="form-input" required value={draft.name}
              onChange={e => setDraft({ ...draft, name: e.target.value })}
              placeholder="e.g. Headlights, Brake fluid level" />
          </label>
          <label>
            <div style={lblStyle}>Category</div>
            <input className="form-input" value={draft.category}
              onChange={e => setDraft({ ...draft, category: e.target.value })}
              placeholder="general" />
          </label>
          <label>
            <div style={lblStyle}>Response Type</div>
            <select className="form-input" value={draft.response_type}
              onChange={e => setDraft({ ...draft, response_type: e.target.value })}>
              {RESPONSE_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </label>
          <label>
            <div style={lblStyle}>Sort Order</div>
            <input className="form-input" type="number" value={draft.sort_order}
              onChange={e => setDraft({ ...draft, sort_order: e.target.value })} />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={draft.allow_na}
              onChange={e => setDraft({ ...draft, allow_na: e.target.checked })} />
            <span>Allow N/A</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={draft.is_critical}
              onChange={e => setDraft({ ...draft, is_critical: e.target.checked })} />
            <span style={{ color: '#dc2626', fontWeight: 700 }}>Critical — fail grounds truck</span>
          </label>
          <div style={{ gridColumn: '1 / -1', textAlign: 'right' }}>
            <button type="submit" className="btn btn-gold" disabled={savingId === 'new'}>
              {savingId === 'new' ? 'Saving…' : 'Save Item'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
      ) : (
        <>
          <ItemTable
            label="Active Items"
            items={active}
            savingId={savingId}
            onToggleCritical={item => toggleField(item, 'is_critical')}
            onToggleAllowNA={item => toggleField(item, 'allow_na')}
            onArchive={archive}
            archived={false}
          />
          {archived.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <ItemTable
                label="Archived"
                items={archived}
                savingId={savingId}
                onToggleCritical={item => toggleField(item, 'is_critical')}
                onToggleAllowNA={item => toggleField(item, 'allow_na')}
                onArchive={unarchive}
                archived={true}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ItemTable({ label, items, savingId, onToggleCritical, onToggleAllowNA, onArchive, archived }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', fontWeight: 700, color: '#475569' }}>
        {label} <span style={{ color: '#94a3b8', fontWeight: 400 }}>({items.length})</span>
      </div>
      {items.length === 0 ? (
        <div style={{ padding: 16, color: 'var(--text-muted)', textAlign: 'center' }}>None.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <th style={th}>Name</th>
              <th style={th}>Category</th>
              <th style={th}>Response</th>
              <th style={{ ...th, textAlign: 'center' }}>Allow N/A</th>
              <th style={{ ...th, textAlign: 'center' }}>Critical</th>
              <th style={{ ...th, textAlign: 'right' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={td}>
                  <strong>{item.name}</strong>
                  {item.is_critical ? (
                    <span style={{
                      marginLeft: 8, padding: '1px 6px', borderRadius: 4,
                      background: '#fee2e2', color: '#991b1b',
                      fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                    }}>Critical</span>
                  ) : null}
                </td>
                <td style={td}>{item.category || '—'}</td>
                <td style={td}>
                  {RESPONSE_TYPES.find(r => r.value === item.response_type)?.label || item.response_type}
                </td>
                <td style={{ ...td, textAlign: 'center' }}>
                  <input type="checkbox" checked={!!item.allow_na}
                    disabled={savingId === item.id || archived}
                    onChange={() => onToggleAllowNA(item)} />
                </td>
                <td style={{ ...td, textAlign: 'center' }}>
                  <input type="checkbox" checked={!!item.is_critical}
                    disabled={savingId === item.id || archived}
                    onChange={() => onToggleCritical(item)} />
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <button type="button" className="btn btn-sm btn-outline"
                    disabled={savingId === item.id}
                    onClick={() => onArchive(item)}>
                    {archived ? 'Restore' : 'Archive'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const lblStyle = { fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 };
const th = { textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 };
const td = { padding: '10px 12px', verticalAlign: 'middle' };
